'use client'

import { useCallback, useMemo, useState } from 'react'
import { useField, useForm, useFormFields } from '@payloadcms/ui'
import { useMediaUrl } from './useMediaUrl'
import type { HotspotType } from './types'

// Equirectangular projection: yaw 0 = image horizontal center, pitch 0 = image vertical center.
function yawPitchToPercent(yaw: number, pitch: number) {
  const x = ((yaw ?? 0) / (2 * Math.PI) + 0.5) * 100
  const y = (0.5 - (pitch ?? 0) / Math.PI) * 100
  return { x, y }
}

function percentToYawPitch(xPct: number, yPct: number) {
  const yaw = (xPct / 100 - 0.5) * 2 * Math.PI
  const pitch = (0.5 - yPct / 100) * Math.PI
  return { yaw, pitch }
}

const PIN_COLORS: Record<HotspotType, string> = { scene: '#4a90d9', info: '#e0a72f', link: '#7a4ae0' }

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 6, fontSize: 11, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9a9a9a',
}
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 10px', border: '1px solid #d0d0d0', borderRadius: 4,
  fontSize: 13, marginBottom: 6, boxSizing: 'border-box', fontFamily: 'inherit',
}

type Row = {
  rowIndex: number
  id: string
  yaw: number
  pitch: number
  type: HotspotType
  targetScene?: string
  label?: string
  infoText?: string
  linkUrl?: string
}

export function HotspotEditorField({ path }: { path: string }) {
  const { rows } = useField<unknown>({ path, hasRows: true })
  const { addFieldRow, removeFieldRow, dispatchFields, setModified } = useForm()

  const panoramaPath = path.replace(/\.hotspots$/, '.panorama')
  const scenesBasePathMatch = path.match(/^(.*)\.\d+\.hotspots$/)
  const scenesBasePath = scenesBasePathMatch ? scenesBasePathMatch[1] : ''

  const allFields = useFormFields(([fields]) => fields)
  const panoramaUrl = useMediaUrl(allFields?.[panoramaPath]?.value)

  const sceneRowsState = useField<unknown>({ path: scenesBasePath, hasRows: true })
  const sceneRows = sceneRowsState.rows ?? []

  const sceneOptions = useMemo(() => {
    if (!scenesBasePath) return []
    return sceneRows.map((r, i) => {
      const title = allFields?.[`${scenesBasePath}.${i}.title`]?.value as string | undefined
      return { id: (r as { id: string }).id, title: title || `Scene ${i + 1}` }
    })
  }, [sceneRows, allFields, scenesBasePath])

  const hotspots: Row[] = useMemo(() => {
    return (rows || []).map((r, i) => {
      const get = (name: string) => allFields?.[`${path}.${i}.${name}`]?.value
      return {
        rowIndex: i,
        id: (r as { id: string }).id,
        yaw: (get('yaw') as number) ?? 0,
        pitch: (get('pitch') as number) ?? 0,
        type: ((get('type') as HotspotType) || 'scene'),
        targetScene: get('targetScene') as string | undefined,
        label: get('label') as string | undefined,
        infoText: get('infoText') as string | undefined,
        linkUrl: get('linkUrl') as string | undefined,
      }
    })
  }, [rows, allFields, path])

  const [selected, setSelected] = useState<number | null>(null)
  const [dragging, setDragging] = useState<number | null>(null)

  const updateField = useCallback((rowIndex: number, name: string, value: unknown) => {
    dispatchFields({ type: 'UPDATE', path: `${path}.${rowIndex}.${name}`, value })
    setModified(true)
  }, [dispatchFields, path, setModified])

  const handleAddAt = useCallback((xPct: number, yPct: number) => {
    const { yaw, pitch } = percentToYawPitch(xPct, yPct)
    const newIndex = (rows || []).length
    addFieldRow({ path, rowIndex: newIndex, schemaPath: path })
    setTimeout(() => {
      dispatchFields({ type: 'UPDATE', path: `${path}.${newIndex}.yaw`, value: yaw })
      dispatchFields({ type: 'UPDATE', path: `${path}.${newIndex}.pitch`, value: pitch })
      dispatchFields({ type: 'UPDATE', path: `${path}.${newIndex}.type`, value: 'scene' })
      setModified(true)
      setSelected(newIndex)
    }, 0)
  }, [rows, path, addFieldRow, dispatchFields, setModified])

  const handleStageClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (dragging !== null) return
    const rect = e.currentTarget.getBoundingClientRect()
    const xPct = ((e.clientX - rect.left) / rect.width) * 100
    const yPct = ((e.clientY - rect.top) / rect.height) * 100
    handleAddAt(xPct, yPct)
  }, [dragging, handleAddAt])

  const handleStageMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (dragging === null) return
    const rect = e.currentTarget.getBoundingClientRect()
    const xPct = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100))
    const yPct = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100))
    const { yaw, pitch } = percentToYawPitch(xPct, yPct)
    updateField(dragging, 'yaw', yaw)
    updateField(dragging, 'pitch', pitch)
  }, [dragging, updateField])

  const stopDragging = useCallback(() => setDragging(null), [])

  const handleDelete = useCallback((rowIndex: number) => {
    removeFieldRow({ path, rowIndex })
    setSelected(null)
  }, [removeFieldRow, path])

  const active = selected !== null ? hotspots[selected] : null

  return (
    <div style={{ marginBottom: 12 }}>
      <label style={labelStyle}>Hotspots — click the panorama to place a pin, drag to reposition</label>

      {!panoramaUrl ? (
        <div style={{ padding: 16, fontSize: 12, color: '#9a9a9a', border: '1px dashed #d0d0d0', borderRadius: 6 }}>
          Upload a panorama above first.
        </div>
      ) : (
        <div
          onMouseMove={handleStageMouseMove}
          onMouseUp={stopDragging}
          onMouseLeave={stopDragging}
          onClick={handleStageClick}
          style={{
            position: 'relative', width: '100%', borderRadius: 6, overflow: 'hidden',
            border: '1px solid #d0d0d0', cursor: dragging !== null ? 'grabbing' : 'crosshair', lineHeight: 0,
          }}
        >
          <img src={panoramaUrl} alt="" style={{ display: 'block', width: '100%', userSelect: 'none' }} draggable={false} />
          {hotspots.map((h, i) => {
            const { x, y } = yawPitchToPercent(h.yaw, h.pitch)
            return (
              <button
                key={h.id || i}
                type="button"
                onMouseDown={e => { e.stopPropagation(); setSelected(i); setDragging(i) }}
                onClick={e => e.stopPropagation()}
                title={h.label || h.type}
                style={{
                  position: 'absolute', left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)',
                  width: 22, height: 22, borderRadius: '50%', border: '2px solid #fff',
                  background: i === selected ? '#e05f5f' : PIN_COLORS[h.type] || '#4a90d9',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.45)', cursor: 'grab', padding: 0,
                }}
              />
            )
          })}
        </div>
      )}

      {active && (
        <div style={{ border: '1px solid #d0d0d0', borderRadius: 6, padding: 10, marginTop: 8, background: 'rgba(0,0,0,0.02)' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <select
              value={active.type}
              onChange={e => updateField(active.rowIndex, 'type', e.target.value)}
              style={{ ...inputStyle, marginBottom: 0, width: 'auto' }}
            >
              <option value="scene">Go to scene</option>
              <option value="info">Info</option>
              <option value="link">Link to page</option>
            </select>
            <button
              type="button"
              onClick={() => handleDelete(active.rowIndex)}
              style={{ marginLeft: 'auto', color: '#e05f5f', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}
            >
              Delete
            </button>
          </div>

          <input
            placeholder="Label (e.g. Enter Main Hall)"
            value={active.label || ''}
            onChange={e => updateField(active.rowIndex, 'label', e.target.value)}
            style={inputStyle}
          />

          {active.type === 'scene' && (
            <select
              value={active.targetScene || ''}
              onChange={e => updateField(active.rowIndex, 'targetScene', e.target.value)}
              style={inputStyle}
            >
              <option value="">— choose target scene —</option>
              {sceneOptions.map(s => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
          )}

          {active.type === 'info' && (
            <textarea
              placeholder="Info text shown in the popup"
              value={active.infoText || ''}
              onChange={e => updateField(active.rowIndex, 'infoText', e.target.value)}
              style={{ ...inputStyle, minHeight: 60 }}
            />
          )}

          {active.type === 'link' && (
            <input
              placeholder="/people/aksel-bakunts or https://…"
              value={active.linkUrl || ''}
              onChange={e => updateField(active.rowIndex, 'linkUrl', e.target.value)}
              style={inputStyle}
            />
          )}
        </div>
      )}

      {hotspots.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {hotspots.map((h, i) => (
            <button
              key={h.id || i}
              type="button"
              onClick={() => setSelected(i)}
              style={{
                fontSize: 11, padding: '4px 8px', borderRadius: 999, cursor: 'pointer',
                border: i === selected ? '1px solid #e05f5f' : '1px solid #d0d0d0',
                background: '#fff', color: '#333',
              }}
            >
              {h.label || `${h.type} #${i + 1}`}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
